import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useDarkMode } from '../Functions/DarkMode';
import { DateRange } from 'react-date-range';
import { addDays } from 'date-fns';
import { enUS } from 'date-fns/locale'; // Add locale import
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const CheckinEnquiryForm = forwardRef(({ 
  isEmbedded = false, 
  onSubmitEnquiry = null,
  className = '',
  propertyName = '',
  propertyLocation = '',
  isSubmitting = false
}, ref) => {
    // Add form reference for email submission
    const formRef = useRef(null);
    const hiddenFormRef = useRef(null);
    
    // Access dark mode state
    const { darkMode } = useDarkMode();
    
    // Get today and tomorrow dates
    const today = new Date();
    const tomorrow = addDays(today, 1);
    
    const formatDateForInput = (date) => {
        return date.toISOString().split('T')[0];
    };
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        adults: 1,
        children: 0
    });

    // Add validation state
    const [validation, setValidation] = useState({
        email: true,
        phone: true
    });

    // Add validation messages
    const [validationMessages, setValidationMessages] = useState({
        email: '',
        phone: ''
    });

    // Date range state using the format required by react-date-range
    const [dateState, setDateState] = useState([
        {
            startDate: today,
            endDate: tomorrow,
            key: 'selection'
        }
    ]);
    const [showCalendar, setShowCalendar] = useState(false);
    
    // Add checkbox state for agreement
    const [agreed, setAgreed] = useState(false);
    const [showAgreementError, setShowAgreementError] = useState(false);
    
    // Expose resetForm method to parent component
    useImperativeHandle(ref, () => ({
        resetForm: () => {
            setFormData({
                name: '',
                email: '',
                phone: '',
                adults: 1,
                children: 0
            });
            setDateState([
                {
                    startDate: today,
                    endDate: tomorrow,
                    key: 'selection'
                }
            ]);
        }
    }));
    
    // Validate email format
    const validateEmail = (email) => {
        if (!email) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Validate phone number (basic validation for numeric input with optional dashes/spaces)
    const validatePhone = (phone) => {
        if (!phone) return false;
        const phoneRegex = /^[0-9\s\-\+\(\)]{6,15}$/;
        return phoneRegex.test(phone);
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        // Check agreement checkbox
        if (!agreed) {
            setShowAgreementError(true);
            return;
        } else {
            setShowAgreementError(false);
        }

        // Check if all required fields are filled
        if (!formData.name || !formData.email || !formData.phone) {
            setValidation({
                email: !!formData.email && validateEmail(formData.email),
                phone: !!formData.phone && validatePhone(formData.phone)
            });
            
            setValidationMessages({
                email: !formData.email ? 'Email is required' : (!validateEmail(formData.email) ? 'Please enter a valid email address' : ''),
                phone: !formData.phone ? 'Phone number is required' : (!validatePhone(formData.phone) ? 'Please enter a valid phone number' : '')
            });
            
            return;
        }
        
        // Final validation check before submission
        const isEmailValid = validateEmail(formData.email);
        const isPhoneValid = validatePhone(formData.phone);
        
        setValidation({
            email: isEmailValid,
            phone: isPhoneValid
        });
        
        setValidationMessages({
            email: isEmailValid ? '' : 'Please enter a valid email address',
            phone: isPhoneValid ? '' : 'Please enter a valid phone number'
        });
        
        // Only proceed if all validations pass
        if (isEmailValid && isPhoneValid) {
            // Complete form data including dates
            const completeFormData = {
                ...formData,
                checkInDate: formatDateForInput(dateState[0].startDate),
                checkOutDate: formatDateForInput(dateState[0].endDate)
            };
            
            console.log('Enquiry submitted:', completeFormData);
            
            // If a custom submit handler was provided via props, call it
            if (onSubmitEnquiry) {
                onSubmitEnquiry(completeFormData, formRef);
            }
        }
    };

    // Handle input changes with validation
    const handleChange = (e) => {
        const { name, value } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Validate fields as user types
        if (name === 'email') {
            const isValid = value === '' || validateEmail(value);
            setValidation(prev => ({ ...prev, email: isValid }));
            setValidationMessages(prev => ({ 
                ...prev, 
                email: isValid ? '' : 'Please enter a valid email address'
            }));
        } else if (name === 'phone') {
            const isValid = value === '' || validatePhone(value);
            setValidation(prev => ({ ...prev, phone: isValid }));
            setValidationMessages(prev => ({ 
                ...prev, 
                phone: isValid ? '' : 'Please enter a valid phone number'
            }));
        }
    };
    
    // Handle guest count changes with +/- buttons - no strict limits
    const handleGuestCount = (type, increment) => {
        setFormData(prev => {
            // Calculate new value
            let newValue = prev[type] + increment;
            
            // Only ensure we don't go below 1 for adults and 0 for children
            if (type === 'adults') {
                newValue = Math.max(1, newValue); // Min 1 adult
            } else {
                newValue = Math.max(0, newValue); // Min 0 children
            }
            
            return {
                ...prev,
                [type]: newValue
            };
        });
    };
    
    // Format dates for display
    const formatDisplayDate = (date) => {
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };
    
    // Get formatted date range for display
    const dateRangeText = `${formatDisplayDate(dateState[0].startDate)} - ${formatDisplayDate(dateState[0].endDate)}`;
    
    // Close calendar when clicking outside
    const handleClickOutside = (e) => {
        if (showCalendar && !e.target.closest('.date-range-container')) {
            setShowCalendar(false);
        }
    };
    
    // Add event listener when calendar is open
    useEffect(() => {
        if (showCalendar) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCalendar]);
    
    // Counter component for guest selection with improved styling and alignment
    const GuestCounter = ({ label, type, value, disabled }) => (
        <div className="guest-counter mb-3">
            <div className="d-flex align-items-center">
                <span className="guest-label" style={{ 
                    minWidth: '80px', 
                    fontWeight: '500'
                }}>{label}</span>
                <div className="counter-controls d-flex align-items-center ms-auto">
                    <button 
                        type="button" 
                        className="btn btn-counter"
                        onClick={() => handleGuestCount(type, -1)}
                        disabled={disabled || (type === 'adults' && value <= 1) || (type === 'children' && value <= 0)}
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: darkMode ? '#444' : '#f0f0f0',
                            color: darkMode ? '#fff' : '#333',
                            border: 'none',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <i className="fas fa-minus fa-sm"></i>
                    </button>
                    <span className="mx-3 guest-count" style={{ 
                        minWidth: '30px', 
                        textAlign: 'center',
                        fontWeight: '500' 
                    }}>
                        {value}
                    </span>
                    <button 
                        type="button" 
                        className="btn btn-counter"
                        onClick={() => handleGuestCount(type, 1)}
                        disabled={disabled}
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: darkMode ? '#444' : '#f0f0f0',
                            color: darkMode ? '#fff' : '#333',
                            border: 'none',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <i className="fas fa-plus fa-sm"></i>
                    </button>
                </div>
            </div>
        </div>
    );
    
    // For standalone use (as a searchbar)
    if (!isEmbedded) {
        return (
            <div className={`container mt-4 ${className}`}>
                <form onSubmit={handleSubmit}>
                    <div className="enquiry-form" style={{
                        backgroundColor: darkMode ? '#2d2d2d' : '',
                        color: darkMode ? '#f5f5f5' : '',
                        borderColor: darkMode ? '#444' : ''
                    }}>
                        <div className="row">
                            <div className="col-md-5 mb-3 mb-md-0">
                                <label style={{ color: darkMode ? '#f5f5f5' : '' }}>Check-in / Check-out Dates</label>
                                <div className="position-relative date-range-container">
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{
                                            backgroundColor: darkMode ? '#2d2d2d' : '',
                                            color: darkMode ? '#f5f5f5' : '',
                                            borderColor: darkMode ? '#444' : ''
                                        }}
                                        value={dateRangeText}
                                        onClick={() => !isSubmitting && setShowCalendar(!showCalendar)}
                                        readOnly
                                        disabled={isSubmitting}
                                    />
                                    {showCalendar && (
                                        <div className="position-absolute mt-2 shadow rounded date-range-dropdown" 
                                            style={{ 
                                                zIndex: 1000, 
                                                backgroundColor: darkMode ? '#333' : 'white',
                                                width: 'fit-content',
                                                left: 0
                                            }}
                                        >
                                            <DateRange
                                                onChange={item => setDateState([item.selection])}
                                                moveRangeOnFirstSelection={false}
                                                ranges={dateState}
                                                minDate={today}
                                                color={darkMode ? '#3a86ff' : '#0077cc'}
                                                rangeColors={[darkMode ? '#3a86ff' : '#0077cc']}
                                                months={1}
                                                direction="horizontal"
                                                locale={enUS}
                                                className={darkMode ? 'date-range-dark' : 'date-range-light'}
                                            />
                                            <div className="d-flex justify-content-end p-2">
                                                <button 
                                                    type="button" 
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => setShowCalendar(false)}
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-5 mb-3 mb-md-0">
                                <label style={{ color: darkMode ? '#f5f5f5' : '' }}>Guests</label>
                                <div className="guest-selection px-3 py-2 border rounded" style={{
                                    backgroundColor: darkMode ? '#383838' : '#f9f9f9',
                                    borderColor: darkMode ? '#444' : '#ddd'
                                }}>
                                    <GuestCounter label="Adults" type="adults" value={formData.adults} disabled={isSubmitting} />
                                    <GuestCounter label="Children" type="children" value={formData.children} disabled={isSubmitting} />
                                </div>
                            </div>
                            <div className="col-md-2 d-flex align-items-end">
                                <button 
                                    type="submit" 
                                    className="btn btn-primary w-100"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-paper-plane me-1"></i> Send Enquiry
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
                
                {/* Fix the style tag by converting boolean attributes to strings */}
                <style jsx="true" global="true">{`
                    /* Light mode styles */
                    .date-range-light .rdrCalendarWrapper {
                        background-color: #fff;
                        color: #000;
                        font-size: 14px;
                        border-radius: 4px;
                    }
                    
                    .date-range-light .rdrDateDisplayItem {
                        border-radius: 4px;
                        background-color: #f5f5f5;
                    }
                    
                    .date-range-light .rdrDateDisplayItemActive {
                        border-color: #0077cc;
                    }
                    
                    .date-range-light .rdrDayHovered {
                        background-color: #e6f2ff !important;
                    }
                    
                    .date-range-light .rdrDayToday .rdrDayNumber span:after {
                        background: #0077cc;
                    }
                    
                    .date-range-light .rdrDay:not(.rdrDayPassive) .rdrInRange, 
                    .date-range-light .rdrDay:not(.rdrDayPassive) .rdrStartEdge,
                    .date-range-light .rdrDay:not(.rdrDayPassive) .rdrEndEdge,
                    .date-range-light .rdrDay:not(.rdrDayPassive) .rdrSelected {
                        color: #fff;
                    }
                    
                    .date-range-light .rdrDay:not(.rdrDayPassive) .rdrDayStartPreview, 
                    .date-range-light .rdrDay:not(.rdrDayPassive) .rdrDayEndPreview, 
                    .date-range-light .rdrDay:not(.rdrDayPassive) .rdrDayInPreview {
                        border-color: #0077cc75;
                    }
                    
                    /* Dark mode styles */
                    .date-range-dark .rdrCalendarWrapper {
                        background-color: #333;
                        color: #f5f5f5;
                        font-size: 14px;
                        border-radius: 4px;
                    }
                    
                    .date-range-dark .rdrMonthAndYearWrapper {
                        background-color: #333;
                        color: #f5f5f5;
                    }
                    
                    .date-range-dark .rdrMonthAndYearPickers select {
                        background-color: #444;
                        color: #f5f5f5;
                    }
                    
                    .date-range-dark .rdrMonthAndYearPickers select:hover {
                        background-color: #555;
                    }
                    
                    .date-range-dark .rdrMonths {
                        background-color: #333;
                    }
                    
                    .date-range-dark .rdrMonth {
                        background-color: #333;
                    }
                    
                    .date-range-dark .rdrWeekDay {
                        color: #ccc;
                    }
                    
                    .date-range-dark .rdrDayNumber span {
                        color: #f5f5f5;
                    }
                    
                    .date-range-dark .rdrDayDisabled .rdrDayNumber span {
                        color: #777;
                    }
                    
                    .date-range-dark .rdrDayPassive .rdrDayNumber span {
                        color: #666;
                    }
                    
                    .date-range-dark .rdrDateDisplayWrapper {
                        background-color: #333;
                    }
                    
                    .date-range-dark .rdrDateDisplayItem {
                        background-color: #444;
                        border-color: #555;
                        border-radius: 4px;
                    }
                    
                    .date-range-dark .rdrDateDisplayItemActive {
                        border-color: #3a86ff;
                    }
                    
                    .date-range-dark .rdrDateDisplayItem input {
                        color: #f5f5f5;
                    }
                    
                    .date-range-dark .rdrDayHovered {
                        background-color: #444 !important;
                    }
                    
                    .date-range-dark .rdrDayToday .rdrDayNumber span:after {
                        background: #3a86ff;
                    }
                    
                    .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrInRange, 
                    .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrStartEdge,
                    .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrEndEdge,
                    .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrSelected {
                        color: #fff;
                    }
                    
                    .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrDayStartPreview, 
                    .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrDayEndPreview, 
                    .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrDayInPreview {
                        border-color: #3a86ff75;
                    }
                    
                    /* Mobile responsiveness */
                    @media (max-width: 768px) {
                        .date-range-dropdown {
                            left: 0;
                            right: 0;
                            width: 100% !important;
                        }
                        
                        .rdrCalendarWrapper, 
                        .rdrMonth {
                            width: 100% !important;
                            max-width: 100%;
                        }
                    }
                `}</style>
            </div>
        );
    }
    
    // For embedded use (in booking card)
    return (
        <>
            <form onSubmit={handleSubmit} className={className} ref={formRef}>
                <div className="mb-3">
                    <label className="form-label">Name <span className="text-danger">*</span></label>
                    <input 
                        type="text" 
                        className="form-control" 
                        placeholder="Enter your name" 
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label">Email id <span className="text-danger">*</span></label>
                    <input 
                        type="email" 
                        className={`form-control ${!validation.email ? 'is-invalid' : ''}`}
                        placeholder="Enter your email" 
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                    />
                    {!validation.email && (
                        <div className="invalid-feedback">
                            {validationMessages.email}
                        </div>
                    )}
                </div>
                <div className="mb-3">
                    <label className="form-label">Mobile or WhatsApp number <span className="text-danger">*</span></label>
                    <input 
                        type="tel" 
                        className={`form-control ${!validation.phone ? 'is-invalid' : ''}`}
                        placeholder="Enter your mobile number" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                    />
                    {!validation.phone && (
                        <div className="invalid-feedback">
                            {validationMessages.phone}
                        </div>
                    )}
                </div>
                <div className="mb-3">
                    <label className="form-label">Stay Dates</label>
                    <div className="position-relative date-range-container">
                        <input
                            type="text"
                            className="form-control"
                            value={dateRangeText}
                            onClick={() => !isSubmitting && setShowCalendar(!showCalendar)}
                            readOnly
                            required
                            id="stayDates"
                            name="stayDates"
                            disabled={isSubmitting}
                        />
                        {showCalendar && (
                            <div className="position-absolute mt-2 shadow rounded date-range-dropdown" 
                                style={{ 
                                    zIndex: 1000, 
                                    backgroundColor: darkMode ? '#333' : 'white',
                                    width: 'fit-content',
                                    left: 0
                                }}
                            >
                                <DateRange
                                    onChange={item => setDateState([item.selection])}
                                    moveRangeOnFirstSelection={false}
                                    ranges={dateState}
                                    minDate={today}
                                    color={darkMode ? '#3a86ff' : '#0077cc'}
                                    rangeColors={[darkMode ? '#3a86ff' : '#0077cc']}
                                    months={1}
                                    direction="horizontal"
                                    locale={enUS}
                                    className={darkMode ? 'date-range-dark' : 'date-range-light'}
                                />
                                <div className="d-flex justify-content-end p-2">
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-primary"
                                        onClick={() => setShowCalendar(false)}
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="mb-3">
                    <label className="form-label">Guests</label>
                    <div className="guest-selection p-3 border rounded" style={{
                        backgroundColor: darkMode ? '#383838' : '#f9f9f9',
                        borderColor: darkMode ? '#444' : '#ddd'
                    }}>
                        <GuestCounter label="Adults" type="adults" value={formData.adults} disabled={isSubmitting} />
                        <GuestCounter label="Children" type="children" value={formData.children} disabled={isSubmitting} />
                        <input type="hidden" name="guests" value={`Adults: ${formData.adults}, Children: ${formData.children}`} />
                    </div>
                </div>
                
                {/* Hidden fields for property info */}
                <input type="hidden" name="propertyName" value={propertyName} />
                <input type="hidden" name="propertyLocation" value={propertyLocation} />
                
                {/* Agreement Checkbox */}
                <div className="form-check mb-3">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="agreePolicy"
                        checked={agreed}
                        onChange={e => setAgreed(e.target.checked)}
                        disabled={isSubmitting}
                        required
                    />
                    <label className="form-check-label" htmlFor="agreePolicy">
                        I agree to the <a href="/policy" target="_blank" rel="noopener noreferrer">Payment &amp; Cancellation/Refund Policy</a>
                    </label>
                </div>
                {showAgreementError && (
                    <div className="text-danger mb-2" style={{ fontSize: '0.95em' }}>
                        You must agree to the Payment and Cancellation/Refund Policy before submitting.
                    </div>
                )}

                <button 
                    type="submit" 
                    className="btn btn-primary w-100 py-3"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-paper-plane me-2"></i> Send My Enquiry
                        </>
                    )}
                </button>
            </form>
            
            {/* Hidden form for FormSubmit email service */}
            <div style={{ display: 'none' }}>
                <form 
                    ref={hiddenFormRef}
                    action="https://formsubmit.co/info@lsqstays.com" 
                    method="POST"
                >
                    <input type="hidden" name="_captcha" value="false" />
                    <input type="hidden" name="_template" value="table" />
                    <input type="hidden" name="_subject" value={`New Booking Enquiry - ${propertyName || 'LSQ Stays'}`} />
                    
                    <input type="hidden" name="name" value={formData.name} />
                    <input type="hidden" name="email" value={formData.email} />
                    <input type="hidden" name="phone" value={formData.phone} />
                    <input type="hidden" name="stayDates" value={dateRangeText} />
                    <input type="hidden" name="guests" value={`Adults: ${formData.adults}, Children: ${formData.children}`} />
                    <input type="hidden" name="propertyName" value={propertyName} />
                    <input type="hidden" name="propertyLocation" value={propertyLocation} />
                    <input type="hidden" name="enquirySource" value="Property Page Booking Form" />
                </form>
            </div>
            
            {/* Fix the style tag by converting boolean attributes to strings */}
            <style jsx="true" global="true">{`
                /* Light mode styles */
                .date-range-light .rdrCalendarWrapper {
                    background-color: #fff;
                    color: #000;
                    font-size: 14px;
                    border-radius: 4px;
                }
                
                .date-range-light .rdrDateDisplayItem {
                    border-radius: 4px;
                    background-color: #f5f5f5;
                }
                
                .date-range-light .rdrDateDisplayItemActive {
                    border-color: #0077cc;
                }
                
                .date-range-light .rdrDayHovered {
                    background-color: #e6f2ff !important;
                }
                
                .date-range-light .rdrDayToday .rdrDayNumber span:after {
                    background: #0077cc;
                }
                
                .date-range-light .rdrDay:not(.rdrDayPassive) .rdrInRange, 
                .date-range-light .rdrDay:not(.rdrDayPassive) .rdrStartEdge,
                .date-range-light .rdrDay:not(.rdrDayPassive) .rdrEndEdge,
                .date-range-light .rdrDay:not(.rdrDayPassive) .rdrSelected {
                    color: #fff;
                }
                
                .date-range-light .rdrDay:not(.rdrDayPassive) .rdrDayStartPreview, 
                .date-range-light .rdrDay:not(.rdrDayPassive) .rdrDayEndPreview, 
                .date-range-light .rdrDay:not(.rdrDayPassive) .rdrDayInPreview {
                    border-color: #0077cc75;
                }
                
                /* Dark mode styles */
                .date-range-dark .rdrCalendarWrapper {
                    background-color: #333;
                    color: #f5f5f5;
                    font-size: 14px;
                    border-radius: 4px;
                }
                
                .date-range-dark .rdrMonthAndYearWrapper {
                    background-color: #333;
                    color: #f5f5f5;
                }
                
                .date-range-dark .rdrMonthAndYearPickers select {
                    background-color: #444;
                    color: #f5f5f5;
                }
                
                .date-range-dark .rdrMonthAndYearPickers select:hover {
                    background-color: #555;
                }
                
                .date-range-dark .rdrMonths {
                    background-color: #333;
                }
                
                .date-range-dark .rdrMonth {
                    background-color: #333;
                }
                
                .date-range-dark .rdrWeekDay {
                    color: #ccc;
                }
                
                .date-range-dark .rdrDayNumber span {
                    color: #f5f5f5;
                }
                
                .date-range-dark .rdrDayDisabled .rdrDayNumber span {
                    color: #777;
                }
                
                .date-range-dark .rdrDayPassive .rdrDayNumber span {
                    color: #666;
                }
                
                .date-range-dark .rdrDateDisplayWrapper {
                    background-color: #333;
                }
                
                .date-range-dark .rdrDateDisplayItem {
                    background-color: #444;
                    border-color: #555;
                    border-radius: 4px;
                }
                
                .date-range-dark .rdrDateDisplayItemActive {
                    border-color: #3a86ff;
                }
                
                .date-range-dark .rdrDateDisplayItem input {
                    color: #f5f5f5;
                }
                
                .date-range-dark .rdrDayHovered {
                    background-color: #444 !important;
                }
                
                .date-range-dark .rdrDayToday .rdrDayNumber span:after {
                    background: #3a86ff;
                }
                
                .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrInRange, 
                .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrStartEdge,
                .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrEndEdge,
                .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrSelected {
                    color: #fff;
                }
                
                .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrDayStartPreview, 
                .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrDayEndPreview, 
                .date-range-dark .rdrDay:not(.rdrDayPassive) .rdrDayInPreview {
                    border-color: #3a86ff75;
                }
                
                /* Mobile responsiveness */
                @media (max-width: 768px) {
                    .date-range-dropdown {
                        left: 0;
                        right: 0;
                        width: 100% !important;
                    }
                    
                    .rdrCalendarWrapper, 
                    .rdrMonth {
                        width: 100% !important;
                        max-width: 100%;
                    }
                }
            `}</style>
        </>
    );
});

// Add display name for the forwarded ref component
CheckinEnquiryForm.displayName = 'CheckinEnquiryForm';

export default CheckinEnquiryForm;